<template>
    <div class="container-fluid mb-5">

        <div class="qi-page mb-5" v-if="userDetails.affiliate && userDetails.affiliate.affiliate_code">
            <div class="qi-page-header d-flex justify-content-between align-items-start flex-wrap">
                <div>
                    <h4 class="mb-0 text-primary">Affiliate Dashboard</h4>
                    <small class="text-muted">Track your referrals, earnings, and commission.</small>
                </div>
                <a v-if="userDetails.affiliate" v-clipboard:copy="($platformBrand.appUrl + '/').clipboardCode" v-clipboard:success="onCopy" v-clipboard:error="onError" v-clipboard="$platformBrand.appUrl + '/' + userDetails.affiliate.affiliate_code" href="#" class="btn btn-primary rounded" rel="noopener noreferrer"><i class="bi bi-clipboard mr-1"></i>Copy my link</a>
            </div>

            <div v-if="hasLoaded" class="row mb-4" style="row-gap:18px;">
                <div class="col-6 col-md-4 qi-kpi-divider">
                    <div class="text-secondary mb-1" style="font-size:12px;"><i class="bi bi-buildings mr-1"></i>Sign ups</div>
                    <span style="font-size:18px;font-weight:700;letter-spacing:-0.2px;color:#0f172a;">{{ companiesTable.items.length }}</span>
                </div>
                <div class="col-6 col-md-4 qi-kpi-divider qi-kpi-divider--desktop-only">
                    <div class="text-secondary mb-1" style="font-size:12px;"><i class="bi bi-cash-stack mr-1"></i>Earnings</div>
                    <span style="font-size:18px;font-weight:700;letter-spacing:-0.2px;color:#0f172a;">{{ convertCurrencyToShillings(affiliateCompanies.earnings) }}</span>
                </div>
                <div class="col-12 col-md-4">
                    <div class="text-secondary mb-1" style="font-size:12px;"><i class="bi bi-wallet2 mr-1"></i>Available Commission</div>
                    <span style="font-size:18px;font-weight:700;letter-spacing:-0.2px;color:#0f172a;">{{ convertCurrencyToShillings(affiliateCompanies.wallet ? affiliateCompanies.wallet.amount : 0) }}</span>
                    <div v-if="affiliateCompanies.wallet" class="d-flex mt-1" style="gap:14px;">
                        <a v-if="affiliateCompanies.wallet.amount > 100" href="#" class="small" v-b-modal.make-mpesa-withdrawal>Withdraw</a>
                        <a href="#" class="small text-muted">View recent transactions</a>
                    </div>
                </div>
            </div>
            <div v-else class="text-center py-5"><span class="spinner-border"></span></div>

            <div class="qi-page-header" style="margin-bottom:12px;">
                <h5 class="mb-0 text-dark">All Companies</h5>
            </div>
            <div class="row qi-toolbar">
                <div class="col-6">
                    <input type="text" class="form-control" placeholder="Search by company name" v-model="companiesTable.searchTerm" />
                </div>
                <div class="col-6">
                    <date-range-picker :auto-apply="autoApply" v-model="dateRange" class="custom-width-datepicker" :ranges="false">
                        <template #input="picker" style="width: 100% !important;">
                            <span v-if="picker.startDate !== null && picker.endDate !== null" class="text-dark">{{ FormatDate(dateRange.startDate) }} - {{ FormatDate(picker.endDate) }}</span>
                            <span v-else class="text-muted"><i class="bi bi-calendar-event mr-1"></i>Filter by date</span>
                        </template>
                    </date-range-picker>
                </div>
            </div>

            <div class="table-responsive">
                <b-table
                        class="qi-list-table"
                        @filtered="onFiltered"
                        :fields="companiesTable.fields"
                        :items="filteredItems"
                        :filter="companiesTable.filter"
                        hover>
                    <template #cell(status)="data">
                        <i class="bi bi-circle-fill" :class="(data.item.is_subscription === 0 && data.item.installation_fee === null) ? 'text-danger' : 'text-primary'" style="font-size:10px;"></i>
                    </template>
                    <template #cell(company)="data">
                        <span class="font-weight-bold">{{ data.item.company ? data.item.company.name : 'Unknown company' }}</span>
                    </template>
                    <template #cell(earnings)="data">
                        <span v-if="!data.item.is_subscription && !data.item.installation_fee" class="text-muted">0</span>
                        <span v-else>{{ convertCurrencyToShillings(data.item.is_subscription ? data.item.is_subscription : data.item.installation_fee) }}</span>
                    </template>
                    <template #cell(date_added)="data">
                        {{ data.item.company ? formatDate(data.item.company.created_at, 'hh:mm A DD-MMM-Y') : '—' }}
                    </template>
                    <template #cell(last_activity)="data">
                        <span v-if="data.item.company && data.item.company.user_activity_summary">
                            {{ formatDate(data.item.company.user_activity_summary.created_at, 'hh:mm A DD-MMM-Y') }}
                        </span>
                        <span v-else class="text-danger small">No activity</span>
                    </template>
                </b-table>
            </div>
        </div>



    <div v-else>
            <div class="d-flex justify-content-center">
                <div v-if="onboardStep=== 0">
                    <div class="text-center">
                        <small class="text-primary">Step 1/2</small>
                    </div>
                    <div class="text-center py-5">
                        <h4 class="onboarding-text">Welcome to Dukakit Affiliate program!. </h4>
                        <p>To get started just answer a few quizzes. It's easy as one, two, 10k 💸</p>
                    </div>
                    <button @click="onboardAffiliate" class="btn btn-primary btn-block">Let's get started <i class="bi bi-chevron-right"></i> </button>
                </div>
                <div v-if="onboardStep=== 1">
                    <div class="py-2">
                        <a class="btn btn-outline-dark" @click="onboardOneStepBack"> <i class="bi bi-chevron-left"></i> Back</a>
                    </div>
                    <div class="text-center">
                        <small class="text-primary">Step 2/3</small>
                    </div>
                    <div class="text-center py-5">
                        <h4 class="onboarding-text">Do you own a business?</h4>
                        <small class="">Are you a business owner currently using Dukakit?</small>
                        <div class="py-2 text-left ml-5">
                            <div class="form-check">
                                <input class="form-radio-input" type="radio" value=0 name="usingDukakit" v-model="onboardingQuizes.usingDukakit">
                                <label class="form-radio-label" for="defaultCheck1">
                            Yes
                            </label>
                            </div>
                            <div class="form-check">
                                <input class="form-radio-input" type="radio" value=1 name="usingDukakit" v-model="onboardingQuizes.usingDukakit">
                                <label class="form-radio-label" for="defaultCheck1">
                               No
                            </label>
                            </div>
                        </div>
                    </div>
                    <button @click="onboardAffiliate" class="btn btn-primary btn-block">Continue <i class="bi bi-chevron-right"></i> </button>
                </div>
                <div v-if="onboardStep=== 2">
                    <div class="py-2">
                        <a class="btn btn-outline-dark" @click="onboardOneStepBack"> <i class="bi bi-chevron-left"></i> Back</a>
                    </div>
                    <div class="text-center">
                        <small class="text-primary">Step 3/3</small>
                        <p v-if="errorMessage !==''" class="text-danger">{{errorMessage}}</p>
                    </div>
                    <div class="text-center py-5">
                        <h4 class="onboarding-text">Let's pick a friendly link for you</h4>
                        <small class="">Type in the input below</small>
                        <div class="py-2 text-left ml-1">
                            <div class="form-group">
                                <div class="form-inline">
                                    <input class="form-control" style="width: 90%;" type="text" placeholder="Type name" name="username" v-model="onboardingQuizes.userName">
                                    <div v-if="onboardingQuizes.userName !==''">
                                        <span v-if="nameCheck" class="text-success h3"><i class="bi bi-check"></i></span>
                                        <span v-else class="text-danger h3"><i class="bi bi-x"></i></span>
                                    </div>
                                </div>
                            </div>
                            <small class="text-success" v-if="onboardingQuizes.userName !=='' && nameCheck">{{ $platformBrand.appUrl }}/{{ onboardingQuizes.userName }}</small>
                            <small class="text-danger" v-else-if="onboardingQuizes.userName !=='' && !nameCheck">{{ nameMessage || (onboardingQuizes.userName + ' has been taken') }}</small>
                        </div>
                    </div>
                    <button @click="onboardFinish" class="btn btn-primary btn-block">Continue to dashboard <i class="bi bi-chevron-right"></i> </button>
                </div>
            </div>
        </div>
      <b-modal id="make-mpesa-withdrawal" ref="makeMpesaWithdrawal" hide-footer>
        <div class="text-center" v-if="!messageToWithdraw && affiliateCompanies.wallet">
            <h4>Withdraw to M-pesa</h4>

            <p>The amount will be sent to your M-pesa number</p>
            <hr />
            <form @submit.prevent="withdrawToMpesa">
                <div class="form-group">
                    <input class="form-control" type="number" placeholder="Enter amount" v-model="amountToWithdraw" />

                </div>
                <div class="form-group">
                    <button v-if="(amountToWithdraw < 5001 && amountToWithdraw > 9) || amountToWithdraw > affiliateCompanies.wallet.amount " class="btn btn-primary">Continue to Withdraw - {{ convertCurrencyToLocal(amountToWithdraw) }}</button>
                    <button v-else class="btn btn-secondary" disabled>Continue to Withdraw</button>
                </div>

            </form>
        </div>
        <div class="text-center" v-else>
            <div class="spinner-grow" role="status">
                <span class="sr-only">Loading...</span>
            </div>
            <p class="py-3" v-html="messageToWithdraw"></p>
        </div>
      </b-modal>
        </div>
    </template>

<script>
    import axios from "axios";
    import DateRangePicker from "vue2-daterange-picker/src/components/DateRangePicker";
    import {
        mapGetters
    } from 'vuex';
    export default {
        name: "AdminDashboard",
        components: {
            DateRangePicker,
        },
        data() {
            return {
                autoApply: true,
                hasLoaded: false,
                companiesTable: {
                    items: [],
                    fields: [
                        {
                            key: 'status',
                            sortable: false
                        },
                        {

                            key: 'company',
                            sortable: true,
                            filterByFormatted: true
                        },
                        {

                            key: 'earnings',
                            sortable: true
                        },
                        {

                            key: 'date_added',
                            sortable: true
                        },
                        {

                            key: 'last_activity',
                            sortable: true
                        }
                    ],
                    filter: null,
                    totalRows: 1,
                    filterOn: [],
                    searchTerm: '',
                },
                dateRange: {
                    startDate: null,
                    endDate: null,
                },
                companies: {},
                users: {
                    all: [],
                    active: []
                },
                onboardStep: 0,
                onboardingQuizes: {
                    usingDukakit: false,
                    userName: '',
                },
                affiliateCompanies:{
                    earnings:0,
                    wallet:{},
                    companies:{}
                },
                companiesStats: {
                    companies: {
                        orders: {},
                        user: {},
                    },
                    activeCompanies: '',
                    inActiveCompanies: '',
                    freemiumCompanies: '',
                    allOrdersCount: '',
                    totalFinalOrderCount: '',
                },
                nameCheck: false,
                errorMessage: "",
                affiliateEarnings: 0,
                amountToWithdraw: 0,
                messageToWithdraw: false,
                nameMessage: '',
            }
        },
        mounted() {
            this.getAllCompaniesStats()
            // Only a finished affiliate (has a username) clears the onboarding steps.
            // Joining creates the affiliate row before the username is chosen, so the
            // old "affiliate exists" check wiped the steps mid-onboarding and sent
            // people back to the start on any reload.
            if (this.userDetails.affiliate && this.userDetails.affiliate.affiliate_code) {
                localStorage.removeItem("affiliate_onboarding_steps")
                localStorage.removeItem("affiliate_onboardingresponse")
            } else {
                // The menu's cached user can be up to 5 minutes old; make sure we
                // are not showing onboarding to someone who already finished it.
                this.refreshUserDetails()
            }

            this.companiesTable.totalRows = this.companiesTable.items.length
        },
        watch: {
            "onboardingQuizes.userName": function(before, after) {
                axios.get('/api/check-affiliate-name', {
                    params: {
                        username: this.onboardingQuizes.userName
                    }
                }).then(results => {
                    if (results.data.available === 1) {
                        this.nameCheck = true
                        this.errorMessage = ''
                    } else {
                        this.nameCheck = false
                        this.nameMessage = results.data.message || ''
                    }
                }).catch()
            }
        },
        computed: {
            sortOptions() {
                // Create an options list from our fields
                return this.companiesTable.fields
                    .filter(f => f.sortable)
                    .map(f => {
                        return {
                            text: f.label,
                            value: f.key
                        }
                    })
            },
            filteredItems() {
                // item.company can be null - the affiliate transaction's
                // referred company may have been deleted since. Such rows
                // just never match a search term instead of crashing.
                return this.companiesTable.items.filter(item =>
                    (item.company ? item.company.name : '').toLowerCase().includes(this.companiesTable.searchTerm.toLowerCase())
                );
            },
            ...mapGetters(['userDetails'])
        },
        methods: {
            onboardAffiliate() {
                localStorage.setItem("affiliate_onboarding_steps", parseInt(this.onboardStep + 1))
                localStorage.setItem("affiliate_onboardingresponse", JSON.stringify({ ...this.onboardingQuizes
                }))
                this.getCurrentOnboardStatus()
            },
            onboardFinish() {
                if (this.nameCheck && this.errorMessage === '') {
                    this.errorMessage = ''
                    localStorage.setItem("affiliate_onboardingresponse", JSON.stringify({ ...this.onboardingQuizes
                    }))
                    localStorage.setItem("affiliate_onboarding_steps", parseInt(this.onboardStep + 1))
                    this.getCurrentOnboardStatus()
                    axios.post('/api/onboard-affiliate', {
                        response: { ...this.onboardingQuizes }
                    }).then(async results => {
                        // The menu caches /api/user for 5 minutes; with the stale copy
                        // (no affiliate code yet) this page showed onboarding again and
                        // sent the affiliate back to step 1. Refresh it before reloading.
                        await this.refreshUserDetails()
                        localStorage.removeItem("affiliate_onboarding_steps")
                        localStorage.removeItem("affiliate_onboardingresponse")
                        window.location.replace('/affiliates')
                    }).catch(error => {
                        const data = error.response && error.response.data
                        this.errorMessage = (data && data.message) || "There was an error creating your account. Please try again."
                        // Back to the username step so they can fix it.
                        localStorage.setItem("affiliate_onboarding_steps", parseInt(this.onboardStep - 1))
                        this.getCurrentOnboardStatus()
                    })
                } else {
                    this.errorMessage = "There was an error, make sure you have entered a username or the username is available"
                }
            },
            onboardOneStepBack() {
                if (this.onboardStep > 0) {
                    localStorage.setItem("affiliate_onboarding_steps", parseInt(this.onboardStep - 1))
                    this.getCurrentOnboardStatus()
                }
            },
            async refreshUserDetails() {
                try {
                    localStorage.removeItem('apc_user_details_v1')
                    const { data } = await axios.get('/api/user')
                    this.$store.commit('addUserDetails', data)
                    localStorage.setItem('apc_user_details_v1', JSON.stringify({ ts: Date.now(), data }))
                } catch (e) {}
            },
            getCurrentOnboardStatus() {
                this.onboardStep = (localStorage.affiliate_onboarding_steps) ? parseInt(localStorage.affiliate_onboarding_steps) : 0;
            },
            async getAllCompaniesStats() {

                await axios.get('/api/get-affiliates-companies-stats').then(results => {
                    this.affiliateCompanies = results.data

                    this.companiesTable.items = this.affiliateCompanies.companies
                    // this.affiliateEarnings = affiliateCompanies.earnings
                    this.hasLoaded = true

                }).catch(
                    e => {
                        console.log(e)
                    }
                )
            },
            onCopy: function(e) {
                this.copyMessage = 'Copied...'
                this.$toasts.push({
                    'type': 'success',
                    'message': 'Copied successfully'
                })
            },
            onError: function(e) {
                this.$toasts.push({
                    'type': 'error',
                    'message': 'Failed to copy,retry'
                })
            },
            formatDate(date, format) {
                const finaldate = moment(date).format(format);
                return finaldate;
            },
            onFiltered(filteredItems) {
                // Trigger pagination to update the number of buttons/pages due to filtering
                this.companiesTable.totalRows = filteredItems.length
            },
            withdrawToMpesa() {
                axios.post('/api/request-to-withdraw-to-mpesa',{amount:this.amountToWithdraw}).then(results=>{
                    if(results.status === 200 && results.data.type === 'success')
                    {
                        setTimeout(() => {
                            this.messageToWithdraw = "Processing request"
                            window.location.href='/affiliates'
                        }, 3000);

                    }
                    if(results.status === 200 && results.data.type === 'error')
                    {
                       if(results.data.message == "Seems you haven't set an M-pesa phone number. Go to your profile page and update phone number")
                       {
                        this.messageToWithdraw = results.data.message + '&nbsp<a href="/profile">Visit Profile</a>'
                       }
                       else{
                        this.messageToWithdraw = results.data.message
                       }




                    }
                })
            },
        },
    }
</script>

<style scoped>
/* A global unscoped rule elsewhere in the app (employee_earning.vue) sets
   `.small { margin: 150px auto }`, which leaks onto every `.small` element
   site-wide. Neutralize it here (higher specificity than the lone class). */
.small { margin: 0; }

.qi-page { padding: 16px; max-width: 1200px; margin: 0 auto; }
.qi-page-header { margin-bottom: 14px; }
/* Now a Bootstrap .row/.col pair (see template) rather than a flex row -
   columns stay side by side at their breakpoint regardless of content
   width, where flex-wrap was wrapping them whenever the two controls'
   natural widths didn't both fit. */
.qi-toolbar { margin-bottom: 12px; row-gap: 8px; }

/* Flat KPI row, no boxed card - faint right-border divider between
   columns instead (never on the last column of a row). Sign ups/Earnings
   are never last (2-per-row on mobile, 3-per-row on desktop); Earnings'
   divider only applies at md+ since it IS last-in-row on mobile. */
.qi-kpi-divider { border-right: 1px solid #eee; }
@media (max-width: 767.98px) {
    .qi-kpi-divider--desktop-only { border-right: none; }
}

.qi-list-table >>> th { font-size: .75rem; text-transform: uppercase; color: #6c757d; border-top: none; padding: 10px 12px; }
.qi-list-table >>> td { padding: 14px 12px; vertical-align: middle; }
</style>
